import React, { useState, useRef } from "react";
import { TbUpload, TbFileCheck, TbX } from "react-icons/tb";
import { api } from "../../lib/api";
import { useOrganizations } from "../../hooks/useOrganizations";
import { Button, Field, Alert } from "../../components";
import "./UploadScreen.css";

// Upload scan data. Reached from the sidebar, and the same upload is available inside
// an organization row.
//
// Encryption is auto-detected the way the original does: the scanner writes an
// encrypted payload as a quoted string, so a file whose content starts with a double
// quote needs a key. That is a weak test, but changing it here would diverge from what
// the backend expects.

const UploadScreen = () => {
  const { organizations } = useOrganizations();
  const [file, setFile] = useState(null);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [key, setKey] = useState("");
  const [orgId, setOrgId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  // Default to the first organization once they load, matching the original.
  if (!orgId && organizations.length) setTimeout(() => setOrgId(String(organizations[0].id)), 0);

  const take = async (f) => {
    if (!f) return;
    setFile(f); setResult(null); setError("");
    try {
      const head = await f.slice(0, 1).text();
      setIsEncrypted(head.trim().startsWith('"'));
    } catch { setIsEncrypted(false); }
  };

  const submit = async () => {
    if (!file || !orgId) return;
    setBusy(true); setError(""); setResult(null);
    try {
      // FormData goes up without a Content-Type header. Setting one strips the
      // multipart boundary the browser generates and the upload fails silently.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("decryptionKey", key);
      fd.append("exportFormat", "docx");
      fd.append("organization_id", orgId);
      const data = await api.upload("s2/api/uploadfile", fd);
      setResult(data?.message || "Upload complete.");
      setFile(null); setKey(""); setIsEncrypted(false);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      setError(e.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Upload scan data</h2>
          <p>Take the JSON the Pipsqueak scanner produced and attach it to an organization.</p>
        </div>
      </div>

      <div className="v2-upload">
        <Alert>{error}</Alert>
        {result && <Alert tone="success">{result}</Alert>}

        <div
          className={`v2-drop${dragging ? " on" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") inputRef.current?.click(); }}
        >
          <span className="v2-drop-icon"><TbUpload size={22} /></span>
          <span className="v2-drop-title">Drop the scan JSON here</span>
          <span className="v2-drop-hint">or <b>browse your files</b> · .json only</span>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => take(e.target.files?.[0])}
          />
        </div>

        {file && (
          <div className="v2-filechip">
            <TbFileCheck size={15} color="var(--v2-success)" />
            <span className="v2-filechip-name">{file.name}</span>
            <span className="v2-filechip-size">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
            <Button icon aria-label="Remove file" onClick={() => { setFile(null); setIsEncrypted(false); if (inputRef.current) inputRef.current.value = ""; }}>
              <TbX size={14} />
            </Button>
          </div>
        )}

        {isEncrypted && (
          <Field label="Decryption key" hint="The scanner encrypts its output when a key was set at scan time.">
            <input className="v2-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} />
          </Field>
        )}

        <Field label="Organization">
          <select className="v2-input" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>

        <div className="v2-upload-actions">
          <Button variant="primary" onClick={submit} disabled={busy || !file || !orgId || (isEncrypted && !key)}>
            {busy ? "Uploading…" : "Upload and process"}
          </Button>
          {isEncrypted && !key && <span className="v2-hint">Enter the decryption key to continue.</span>}
        </div>
      </div>
    </div>
  );
};

export default UploadScreen;
